import React, { useEffect, useRef, useState } from 'react';
import { Slider } from '@mui/material';
import { SliderThumb } from '@mui/material/Slider';
import { makeStyles } from 'tss-react/mui';
import clsx from 'clsx';
import { decimalToString } from '../../utils/formatNumber';

const useStyles = makeStyles()((theme, { trackColor } = {}) => ({
  root: {
    height: 4,
    padding: 'var(--my-slider-padding, 13px 0)',
    marginLeft: 'var(--my-slider-margin-left, 6px)',
    width: 'var(--my-slider-width, 95%)',
    // Keep the same vertical touch target even if DevTools/device emulation
    // flips the browser between fine and coarse pointer modes.
    '@media (pointer: coarse)': {
      padding: 'var(--my-slider-padding, 13px 0)'
    }
  },
  thumb: {
    height: 20,
    width: 20,
    backgroundColor: '#fff',
    border: '2px solid currentColor',
    boxShadow: '#ebebeb 0 2px 2px',
    '&:focus, &:hover, &.Mui-active': {
      boxShadow: '#ccc 0 2px 3px 1px'
    },
    '& .bar': {
      height: 7,
      width: 1,
      backgroundColor: 'currentColor',
      marginLeft: 1,
      marginRight: 1
    }
  },
  // MUI v9: all visual styling is on the root valueLabel span (background,
  // borderRadius, padding). The old v4 pattern of targeting `& > span`
  // (valueLabelCircle) no longer shapes the pill — it only reaches the inner
  // structural span. Apply pill styles directly here and suppress the v9
  // default ::before arrow-pointer.
  valueLabel: {
    top: -6,
    fontSize: '1.25rem',
    borderRadius: 8,
    padding: '4px 8px',
    lineHeight: '1.4em',
    fontWeight: '500',
    background: 'rgba(97, 97, 97, 0.9)',
    '&::before': {
      display: 'none'
    },
    '& span': {
      whiteSpace: 'nowrap'
    }
  },
  track: {
    color: trackColor,
    height: 4,
    borderRadius: 8
  },
  rail: {
    height: 4,
    borderRadius: 8,
    backgroundColor: '#3966f880'
  },
  mark: {
    backgroundColor: '#fff',
    border: '2px solid #e9e9e9',
    borderRadius: '50%',
    height: 8,
    width: 8,
    marginTop: -2
  },
  markActive: {
    borderColor: '#96dbfa'
  },
  markLabel: {
    top: 15,
    fontSize: 14
  }
}));

function AirbnbThumbComponent(props) {
  const { children, ...other } = props;
  return (
    <SliderThumb {...other}>
      {children}
      <span className="bar" />
      <span className="bar" />
      <span className="bar" />
    </SliderThumb>
  );
}

const INTERNAL_CLASS_KEYS = ['root', 'thumb', 'valueLabel', 'track', 'rail', 'mark', 'markActive', 'markLabel'];

const MySlider = props => {
  const {
    classes = {},
    className,
    steps,
    min,
    max,
    value,
    onChange,
    onChangeCommitted,
    stopPropagation,
    timedUpdateDelay,
    getTrackColor,
    ...rest
  } = props;

  const [internalValue, setInternalValue] = useState(value);
  const [isDragging, setIsDragging] = useState(false);
  const _unmounted = useRef(false);
  const timedUpdate = useRef(undefined);
  const { classes: internalClasses, cx } = useStyles({
    trackColor: getTrackColor ? getTrackColor(internalValue) : undefined
  });

  const mergedClasses = { ...classes };
  INTERNAL_CLASS_KEYS.forEach(key => {
    mergedClasses[key] = cx(internalClasses[key], classes[key]);
  });

  const handleBeforeChange = (event, value) => {
    event.preventDefault();
    if (stopPropagation) {
      event.stopPropagation();
    }
    setIsDragging(true);
    setInternalValue(value);
    if (onChange) {
      onChange(event, value);
    }

    if (timedUpdateDelay) {
      if (timedUpdate.current) {
        clearTimeout(timedUpdate.current);
      }
      timedUpdate.current = setTimeout(() => {
        if (!_unmounted.current) {
          if (internalValue === value) {
            onChangeCommitted(event, value);
          }
          timedUpdate.current = undefined;
        }
      }, timedUpdateDelay);
    }
  };

  const handleAfterChange = (event, value) => {
    if (stopPropagation) {
      event.stopPropagation();
    }
    setIsDragging(false);
    if (onChangeCommitted) {
      onChangeCommitted(event, value);
    }
  };

  useEffect(() => {
    return function cleanup() {
      _unmounted.current = true;
    };
  }, []);

  useEffect(() => {
    if (value !== internalValue) {
      setInternalValue(value);
    }
  }, [value]);

  return (
    <Slider
      classes={mergedClasses}
      className={clsx('slider', className)}
      onChange={handleBeforeChange}
      onChangeCommitted={handleAfterChange}
      slots={{ thumb: AirbnbThumbComponent }}
      valueLabelDisplay={isDragging ? 'on' : 'off'}
      min={min}
      max={max}
      step={max !== undefined && min !== undefined && steps !== undefined ? Math.round((max - min) / steps) : undefined}
      {...rest}
      value={internalValue}
    />
  );
};

export default MySlider;
